import React, { useEffect, useState, useCallback, useMemo } from 'react';
import GenericCard from '../Users/GenericCard';
import { secureGet, securePost, secureDelete } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import { PrimaryButton } from '../../components/PrimaryButton';
import PageScroller from '../../components/PageScroller';
import DeletePopup from '../../components/DeletePopup';
import Filter from '../../components/Filter';
import OptionsMenu from '../../components/OptionsMenu';
import ModuleWrapper from '../../components/ModuleWrapper';
import LoadingLogo from '../../components/LoadingLogo';
import SnackbarNotification from '../../components/SnackbarNotification';

import AddIcon from '../../icons/plusB.svg';
import EditIcon from '../../icons/edit.svg';
import ActivityIcon from '../../icons/slash.svg';
import DeleteIcon from '../../icons/trashR.svg';
import checkIcon from '../../icons/checkGr.svg';

const Repositories = () => {
  const [repositories, setRepositories] = useState([]);
  const [filteredRepositories, setFilteredRepositories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [filters, setFilters] = useState({
    needle: "",
    active: "",
  });
  const [showModule, setShowModule] = useState(false);
  const [selectedRepository, setSelectedRepository] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { t } = useTranslation();

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;
  
  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const STATUS_COLORS = {
    true: "#0AD69F",
    false: "#EF466F",
  };

  const getRepoStatusLabel = (isActive) =>
    isActive ? t("repo_status.active") : t("repo_status.inactive");


  const handleAddClick = () => {
    setSelectedRepository(null);
    setShowModule(true);
  };

  const handleEditClick = (repository) => {
    setSelectedRepository(repository);
    setShowModule(true);
  };


  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "active",
      label: t(`filters.repositories.status`),
      type: "dropdown",
      options: [
        { value: "Activé", label: t(`repo_status.active`) },
        { value: "Désactivé", label: t(`repo_status.inactive`) },
      ],
    },
  ];


  const fetchRepositories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await secureGet('/repos', showSnackbar, { signal });
      let data = Array.isArray(response.data) ? response.data : [];

      data = data.sort((a, b) => {
        if (b.active !== a.active) return Number(b.active) - Number(a.active);
        return a.name.localeCompare(b.name);
      });

      setRepositories(data);
      setFilteredRepositories(data);
      setTotalPages(Math.ceil(data.length / pageSize));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching repositories:', error);
      }
      setRepositories([]);
      setFilteredRepositories([]);
    } finally {
      setLoading(false);
    }
  }, [signal, pageSize]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchRepositories();

    return () => {
      abortController.abort();
    };
  }, [abortController, fetchRepositories]);


  const handleUpdateStatus = async (repository) => {
    try {
      const updatedStatus = !repository.active;
      await securePost('/repos/updateStatus', {
        active: updatedStatus,
        repoId: repository.id,
      });

      const updatedRepositories = repositories.map((repo) =>
        repo.id === repository.id ? { ...repo, active: updatedStatus } : repo
      );

      setRepositories(updatedRepositories);
      applyFilters(updatedRepositories);
    } catch (error) {
      console.error('Error updating repository status:', error);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      const deleteUrl = `/repos/delete/${itemToDelete.id}`;
      await secureDelete(deleteUrl);

      const updatedRepositories = repositories.filter(
        (repo) => repo.id !== itemToDelete.id
      );

      setRepositories(updatedRepositories);
      applyFilters(updatedRepositories);
    } catch (error) {
      console.error('Error deleting repository:', error);
    } finally {
      setDeletePopupVisible(false);
    }
  };

  const applyFilters = useCallback ((data = repositories) => {
    let filteredData = [...data];

    if (filters.needle) {
      const searchTerm = filters.needle.toLowerCase();
      filteredData = filteredData.filter((repo) =>
        repo.name.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.active) {
      const isActive = filters.active === "Activé";
      filteredData = filteredData.filter((repo) => repo.active === isActive);
    }

    setFilteredRepositories(filteredData);
    setTotalPages(Math.ceil(filteredData.length / pageSize));
    setPageNumber(0);
  }, [filters, pageSize, repositories]);

  const handleFilterChange = (filterName, filterValue) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: filterValue,
    }));
  };

  const resetFilters = () => {
    setFilters({
      needle: "",
      active: "",
    });
    setPageNumber(0);
    applyFilters(repositories);
  };

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setPageNumber(page);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setTotalPages(Math.ceil(filteredRepositories.length / newPageSize));
    setPageNumber(0);
  };

  const paginatedRepositories = filteredRepositories.slice(
    pageNumber * pageSize,
    (pageNumber + 1) * pageSize
  );

  const getMenuOptions = (repository) => [
    {
      label: t(`actions.modify`),
      icon: EditIcon,
      action: () => handleEditClick(repository)
      ,
    },
    {
      label: repository.active ? t(`actions.deactivate`) : t(`actions.activate`),
      icon: repository.active ? ActivityIcon : checkIcon,
      color: repository.active ? '#EF476F' : '#0AD69F',
      action: () => handleUpdateStatus(repository),
    },
    // {
    //   label: 'Supprimer',
    //   icon: DeleteIcon,
    //   color: '#EF476F',
    //   action: () => handleDeleteClick(repository),
    // },
  ];
  const handleModuleClose = (action = "cancel") => {
    if (action === "submit") {

      fetchRepositories();
    }
    else if (action === "cancel") {
    }

    setShowModule(false);
    setSelectedRepository(null);
  };

  useEffect(() => {
    applyFilters();
  }, [filters, repositories, applyFilters]);

  return (
    <div>
      <Helmet>
        <title>GrayFox - {t("titles.repositories")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t(`titles.repositories`)}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t(`primaryButtons.add_repo`)}
            icon={AddIcon}
            onClick={handleAddClick} />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {loading ? (
          <div className="col-span-full flex justify-center">
            <LoadingLogo />
          </div>
        ) : (
          paginatedRepositories.map((repository) => (
            <GenericCard
              key={repository.id}
              title={repository.name || 'Nom non disponible'}
              // subtitle={repository.fields[0]?.value || 'Description non disponible'}
              optionsMenu={
                <div onClick={(e) => e.stopPropagation()}>
                  <OptionsMenu options={getMenuOptions(repository)} />
                </div>
              }
              statusTag={
                <span
                  className="px-2 py-1 rounded-[12px] text-[12px] font-semibold text-white"
                  style={{ backgroundColor: STATUS_COLORS[repository.active] }}
                >
                  {getRepoStatusLabel(repository.active).toUpperCase()}
                </span>
              }
              additionalFields={repository.fields || []}
            />
          ))
        )}
      </div>

      {!loading && paginatedRepositories.length === 0 && (
        <div className="text-center mt-4">
          <p></p>
        </div>
      )}

      <PageScroller
        pageNumber={pageNumber}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {showModule && (
        <ModuleWrapper
          moduleType="repos"
          mode={selectedRepository ? "modify" : "add"}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedRepository || {}}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );

};

export default Repositories;
